import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Files, RefreshCw } from "lucide-react";

import type { ActionCard } from "@/types/actions";
import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Modal } from "@/components/ui/modal";
import { Skeleton } from "@/components/ui/skeleton";
import { ActionQueueLabels } from "@/lib/action-queue";
import { djs } from "@/lib/djs";
import { AgendaLabels } from "@/lib/labels/agenda";
import { FinanceiroLabels, finBrl } from "@/lib/labels/financeiro";
import { ManualLabels } from "@/lib/labels/manuals";
import { MedicoLabels } from "@/lib/labels/medico";
import type { ActionEvidenceEntry } from "@/types/actions";

export function EvidenceModal({
	personId,
	sources,
	title,
	onClose,
}: {
	personId: number;
	sources: ActionCard["sources"];
	title: string;
	onClose: () => void;
}) {
	const { data, isPending, isError, isFetching, refetch } = useQuery(
		orpc.today.evidence.queryOptions({ input: { person_id: personId, sources } }),
	);

	return (
		<Modal
			component="evidence-modal"
			className="max-w-2xl"
			title={title}
			onClose={onClose}
			eyebrow={
				<span>
					{sources.length} {sources.length === 1 && "fonte"}
					{sources.length !== 1 && "fontes"}
				</span>
			}
		>
			{isPending && (
				<div role="status" aria-label="carregando fontes" className="flex flex-col gap-3">
					<Skeleton className="h-24 w-full" />
					<Skeleton className="h-24 w-full" />
				</div>
			)}
			{isError && (
				<div role="alert" className="flex flex-col items-start gap-3">
					<p className="text-sm text-destructive">não foi possível carregar as fontes.</p>
					<Button
						type="button"
						size="sm"
						variant="outline"
						disabled={isFetching}
						onClick={() => refetch()}
					>
						<RefreshCw /> tentar de novo
					</Button>
				</div>
			)}
			{data?.entries.length === 0 && <EmptyState icon={Files} title="nenhuma fonte registrada" />}
			{data !== undefined && data.entries.length > 0 && (
				<ol className="flex flex-col gap-3">
					{data.entries.map((entry, index) => (
						<li key={index}>
							<EvidenceEntry entry={entry} />
						</li>
					))}
				</ol>
			)}
		</Modal>
	);
}

function EvidenceFrame({
	kind,
	at,
	author,
	children,
}: {
	kind: ActionCard["sources"][number]["kind"];
	at?: string | Date | null;
	author?: string | null;
	children: ReactNode;
}) {
	const dated = at !== undefined && at !== null && at !== "";
	const dateLabel =
		typeof at === "string" && at.length === 10
			? djs(at).format("DD/MM/YYYY")
			: djs(at).tz("America/Sao_Paulo").format("DD/MM/YYYY HH:mm");

	return (
		<div
			data-slot="evidence-record"
			data-source={kind}
			className="rounded-lg border border-border p-4"
		>
			<header className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
				<span className="font-medium text-primary">{ActionQueueLabels.source[kind]}</span>
				{dated && <time dateTime={djs(at).toISOString()}>{dateLabel}</time>}
			</header>
			<div className="flex flex-col gap-1.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
				{children}
			</div>
			{!!author && <p className="mt-2 text-xs text-muted-foreground">{author}</p>}
		</div>
	);
}

function EvidenceEntry({ entry }: { entry: ActionEvidenceEntry }) {
	if (entry.kind === "restricted") {
		return (
			<EvidenceFrame kind={entry.source.kind}>
				<p className="text-muted-foreground">acesso da médica</p>
			</EvidenceFrame>
		);
	}

	if (entry.data === undefined) {
		return (
			<EvidenceFrame kind={entry.kind}>
				<p className="text-muted-foreground">registro indisponível</p>
			</EvidenceFrame>
		);
	}

	switch (entry.kind) {
		case "message":
			return (
				<EvidenceFrame
					kind={entry.kind}
					at={entry.data.sent_at}
					author={
						entry.data.direction === "contact" ? "paciente" : entry.data.operator_name || "clínica"
					}
				>
					<p>{entry.data.body}</p>
				</EvidenceFrame>
			);
		case "appointment":
			return (
				<EvidenceFrame kind={entry.kind} at={entry.data.start_at} author={entry.data.author}>
					<p className="font-medium">{AgendaLabels.tipo[entry.data.tipo]}</p>
					<p>{AgendaLabels.status[entry.data.status]}</p>
					{!!entry.data.obs && <p>{entry.data.obs}</p>}
					<p className="text-xs text-muted-foreground">
						{entry.data.source === "pulso" && "registrado no pulso"}
						{entry.data.source !== "pulso" && "importado do GestãoDS"}
					</p>
					{!!entry.data.synced_at && (
						<p className="text-xs text-muted-foreground">
							atualizado em{" "}
							{djs(entry.data.synced_at).tz("America/Sao_Paulo").format("DD/MM/YYYY HH:mm")}
						</p>
					)}
				</EvidenceFrame>
			);
		case "financial_receipt":
			return <FinancialEvidence data={entry.data} />;
		case "clinical_milestone":
			return (
				<EvidenceFrame kind={entry.kind} at={entry.data.occurred_at} author={entry.data.author}>
					<p className="font-medium">{entry.data.title}</p>
					{!!entry.data.detail && <p>{entry.data.detail}</p>}
				</EvidenceFrame>
			);
		case "clinical_section":
			return (
				<EvidenceFrame kind={entry.kind} at={entry.data.updated_at} author={entry.data.author}>
					<p className="font-medium">{MedicoLabels.section[entry.data.section]}</p>
					{entry.data.items.map((item, index) => (
						<p key={index}>{item.value}</p>
					))}
					{entry.data.items.length === 0 && (
						<p className="text-muted-foreground">sem conteúdo registrado</p>
					)}
					<p className="text-xs text-muted-foreground">
						{MedicoLabels.itemOrigin[entry.data.last_origin]}
					</p>
					{entry.data.validated_at !== null && (
						<p className="text-xs text-muted-foreground">
							validado em {djs(entry.data.validated_at).format("DD/MM/YYYY")}
						</p>
					)}
				</EvidenceFrame>
			);
		case "clinical_manual":
			return (
				<EvidenceFrame kind={entry.kind} at={entry.data.updated_at} author={entry.data.author}>
					<p className="font-medium">manual da cirurgia</p>
					<p>{ManualLabels.status[entry.data.status]}</p>
					{entry.data.consultation_date !== null && (
						<p>consulta em {djs(entry.data.consultation_date).format("DD/MM/YYYY")}</p>
					)}
					{entry.data.delivered_at !== null && (
						<p>entregue em {djs(entry.data.delivered_at).format("DD/MM/YYYY")}</p>
					)}
				</EvidenceFrame>
			);
		case "clinical_exam":
			return (
				<EvidenceFrame
					kind={entry.kind}
					at={entry.data.collection_date || entry.data.created_at}
					author={entry.data.laboratory}
				>
					<p>{entry.data.report_name || "exame recebido"}</p>
					<Button asChild size="sm" variant="outline" className="mt-2 self-start">
						<a href={`/exams/file/${entry.data.id}`} target="_blank" rel="noreferrer">
							abrir documento original
						</a>
					</Button>
				</EvidenceFrame>
			);
		case "action_event":
			return (
				<EvidenceFrame
					kind={entry.kind}
					at={entry.data.created_at}
					author={ActionQueueLabels.role[entry.data.actor]}
				>
					<p className="font-medium">{ActionQueueLabels.event[entry.data.type]}</p>
					<p>{entry.data.note}</p>
					{entry.data.type === "transferred" && (
						<p className="text-xs text-muted-foreground">
							responsável: {ActionQueueLabels.role[entry.data.role]}
						</p>
					)}
				</EvidenceFrame>
			);
		case "person":
			return (
				<EvidenceFrame kind={entry.kind}>
					<p className="font-medium">{entry.data.name}</p>
					{!!entry.data.note && <p>{entry.data.note}</p>}
					{entry.data.shared_phone && <p>telefone compartilhado</p>}
				</EvidenceFrame>
			);
	}
}

function FinancialEvidence({
	data,
}: {
	data: NonNullable<Extract<ActionEvidenceEntry, { kind: "financial_receipt" }>["data"]>;
}) {
	const linked = data.bank_transaction_id !== null || data.card_sale_id !== null;

	return (
		<EvidenceFrame kind="financial_receipt" at={data.received_on} author={data.author}>
			<p className="font-medium">{finBrl.format(data.amount_cents / 100)}</p>
			<p>{FinanceiroLabels.receipt.method[data.method]}</p>
			{!!data.description && <p>{data.description}</p>}
			{data.cancelled_at !== null && (
				<p className="text-destructive">
					recebimento cancelado em {djs(data.cancelled_at).format("DD/MM/YYYY")}
				</p>
			)}
			{data.cancelled_at === null && linked && (
				<p className="text-xs text-muted-foreground">comprovante vinculado</p>
			)}
			{data.cancelled_at === null && !linked && (
				<p className="text-xs text-muted-foreground">recebimento registrado pela equipe</p>
			)}
		</EvidenceFrame>
	);
}
